/**
 * PMS ▸ Class Integration (DNV MAD; ortak 2026-10-28; ADR 0132): ayar ve anahtarlar, ekipmanın DNV
 * kodu eşlemesi, önizleme ve doğrulama, aktarımlar, indirme. DNV alan adları (`classcomponent`,
 * `jobno` …) Interface Description'daki gibi İngilizce kalır.
 */

export const classIntegrationEn = {
  title: 'Class Integration',
  description:
    'Send completed PMS work records to DNV (Maintenance Data / MAD) every day, check them before they go and download the same data as JSON, Excel or CSV.',
  error: 'Class integration could not be loaded.',
  keysPending: 'API key pending',
  keysPendingText:
    'Sending to DNV starts when both keys are entered in Settings. Preview and downloads work without keys.',
  disabledText:
    'Daily sending is off. Turn it on in Settings when the keys and company name are ready.',
  vessel: 'Vessel',
  chooseVessel: 'Choose a vessel',
  noVessels: 'No vessel is visible to you.',
  tabs: {
    settings: 'Settings',
    mapping: 'Mapping',
    preview: 'Preview & Validation',
    transfers: 'Transfers',
    download: 'Download',
  },
  settings: {
    connection: 'DNV connection',
    enabled: 'Send daily to DNV',
    companyName: 'Company name (company)',
    companyHint: 'The management company name as DNV knows it; it is part of every record key.',
    source: 'Source system (source)',
    endpoint: 'Endpoint',
    sendHour: 'Daily send time (UTC)',
    save: 'Save Settings',
    saved: 'Settings saved.',
    keys: 'API keys',
    keysHint:
      'Keys are stored encrypted and never shown again. Leave a field empty to keep the current key.',
    subscriptionKey: 'Veracity subscription key (Ocp-Apim-Subscription-Key)',
    accessToken: 'MMC access token (Authorization)',
    keySet: 'Entered {{date}}',
    keyNotSet: 'Not entered',
    saveKeys: 'Save Keys',
    keysSaved: 'Keys saved.',
    removeKey: 'Remove',
    test: 'Test Connection',
    testOk: 'DNV answered {{status}}. The keys work.',
    testFailed: 'DNV did not accept the call ({{code}}).',
    vessels: 'Vessels',
    vesselsHint: 'Only the vessels switched on are sent.',
    readOnly: 'Only users with Settings edit permission can change these settings.',
  },
  vesselColumns: {
    vessel: 'Vessel',
    imo: 'IMO',
    send: 'Send',
    mapped: 'Class items mapped',
    pending: 'Not sent yet',
    last: 'Last transfer',
  },
  mapping: {
    hint: 'Each equipment can carry the DNV class component code (classcomponent) and the DNV VIS code. A component without its own class code uses its parent’s.',
    coverage: '{{mapped}} of {{total}} class-relevant equipment mapped',
    template: 'Download Mapping (Excel)',
    import: 'Import File',
    previewTitle: 'Mapping import preview',
    apply: 'Apply',
    applied: 'Mapping imported.',
    nothing: 'The file changes nothing.',
    inherited: 'From parent: {{code}}',
    save: 'Save',
    saved: 'Saved',
    onlyClass: 'Class-relevant only',
    readOnly: 'You can view the mapping; changing it needs Certificates edit permission.',
    columns: {
      code: 'Code',
      name: 'Equipment',
      sfi: 'SFI',
      classRelevant: 'Class',
      classComponent: 'DNV Class Component',
      visCode: 'DNV VIS Code',
      row: 'Row',
      action: 'Change',
      error: 'Problem',
    },
    actions: { create: 'New', update: 'Change', unchanged: 'No change' },
  },
  preview: {
    from: 'Done from',
    to: 'Done to',
    last12: 'Last 12 months',
    refresh: 'Refresh',
    records: 'Records',
    errors: 'With errors',
    warnings: 'With warnings',
    sent: 'Already sent',
    empty: 'No completed work record in this range.',
    ok: 'Ready',
    sentBadge: 'Sent',
    hint: 'Records with an error are not sent; fix the source (for example the vessel IMO or the job code) and they go in the next run.',
    columns: {
      wo: 'WO No.',
      done: 'Done',
      equipment: 'Equipment',
      jobno: 'jobno',
      jobname: 'jobname',
      classcomponent: 'classcomponent',
      status: 'Validation',
    },
  },
  transfers: {
    send: 'Send Now',
    retry: 'Retry',
    sentResult: 'Transfer finished: {{status}}.',
    empty: 'Nothing has been sent yet.',
    retractions: 'Voided after sending',
    retractionsHint:
      'DNV has no delete call. A record voided after it was sent stays at DNV; tell DNV (mpms@dnv.com) if it must be removed. A correcting record with the same job and date replaces it.',
    columns: {
      started: 'Started',
      trigger: 'Run',
      status: 'Status',
      records: 'Records',
      batches: 'Calls',
      http: 'HTTP',
      error: 'Error',
      next: 'Next attempt',
      record: 'Work record',
      sentAt: 'Sent',
      voidedAt: 'Voided',
      reason: 'Reason',
    },
    trigger: { auto: 'Daily', manual: 'Manual', retry: 'Retry' },
    status: {
      running: 'Running',
      succeeded: 'Sent',
      failed: 'Failed',
      nothing_to_send: 'Nothing to send',
    },
  },
  download: {
    hint: 'The same records as the preview, in the DNV Interface Description column order. Records with errors are included so that they can be corrected by hand.',
    json: 'Download JSON',
    xlsx: 'Download Excel',
    csv: 'Download CSV',
    last12: 'Download last 12 months',
    last12Hint:
      'One click: all completed work records of the last 12 months as MAD JSON (DNV-CP-0206 batch export).',
  },
  issues: {
    IMO_MISSING: 'The vessel has no IMO number.',
    CLASS_COMPONENT_MISSING: 'No DNV class component code (sent without it).',
    JOB_NO_MISSING: 'The job has no code.',
    TRUNCATED: 'Text shortened to the DNV length.',
    REQUIRED: 'Required value missing.',
    INVALID_TYPE: 'Wrong value type.',
    OUT_OF_RANGE: 'Value out of range.',
    INVALID_DATE: 'Invalid date.',
    TOO_LONG: 'Text too long.',
    INVALID_VALUE: 'Value not allowed.',
    EMPTY_FILE: 'The file is empty.',
    MISSING_COLUMN: 'Column missing.',
    DUPLICATE_EQUIPMENT: 'The equipment is in the file twice.',
    INVALID_CODE: 'Use letters, digits and . _ / - only.',
    UNKNOWN_EQUIPMENT: 'No such equipment on this vessel.',
  },
  errors: {
    MAD_DISABLED: 'Daily sending is off. Turn it on in Settings.',
    MAD_KEYS_MISSING: 'API key pending: enter both keys in Settings.',
    MAD_COMPANY_MISSING: 'Enter the company name in Settings.',
    MAD_VESSEL_DISABLED: 'Switch this vessel on in Settings first.',
    MAD_TRANSFER_RUNNING: 'A transfer for this vessel is already running.',
    INVALID_MAD_SETTINGS:
      'Check the settings: the company name is required and the endpoint must be https.',
    INVALID_MAD_CODE: 'Use letters, digits and . _ / - only.',
    ROW_VERSION_CONFLICT: 'Someone else changed this in the meantime. Reload and try again.',
    FORBIDDEN: 'You do not have permission for this.',
    GENERIC: 'Something went wrong. Try again.',
  },
};

export const classIntegrationTr: typeof classIntegrationEn = {
  title: 'Klas Entegrasyonu',
  description:
    'Tamamlanan PMS iş kayıtlarını her gün DNV’ye (Maintenance Data / MAD) gönderin, gitmeden önce denetleyin ve aynı veriyi JSON, Excel ya da CSV olarak indirin.',
  error: 'Klas entegrasyonu yüklenemedi.',
  keysPending: 'API anahtarı bekleniyor',
  keysPendingText:
    'DNV’ye gönderim iki anahtar Ayarlar’a girilince başlar. Önizleme ve indirme anahtarsız çalışır.',
  disabledText: 'Günlük gönderim kapalı. Anahtarlar ve şirket adı hazır olunca Ayarlar’dan açın.',
  vessel: 'Gemi',
  chooseVessel: 'Gemi seçin',
  noVessels: 'Görebildiğiniz gemi yok.',
  tabs: {
    settings: 'Ayarlar',
    mapping: 'Eşleme',
    preview: 'Önizleme ve Doğrulama',
    transfers: 'Aktarımlar',
    download: 'İndir',
  },
  settings: {
    connection: 'DNV bağlantısı',
    enabled: 'Her gün DNV’ye gönder',
    companyName: 'Şirket adı (company)',
    companyHint: 'Yönetim şirketinin DNV’deki adı; her kaydın anahtarının parçasıdır.',
    source: 'Kaynak sistem (source)',
    endpoint: 'Uç adresi',
    sendHour: 'Günlük gönderim saati (UTC)',
    save: 'Ayarları Kaydet',
    saved: 'Ayarlar kaydedildi.',
    keys: 'API anahtarları',
    keysHint:
      'Anahtarlar şifreli saklanır ve bir daha gösterilmez. Mevcut anahtarı korumak için alanı boş bırakın.',
    subscriptionKey: 'Veracity abonelik anahtarı (Ocp-Apim-Subscription-Key)',
    accessToken: 'MMC erişim belirteci (Authorization)',
    keySet: '{{date}} girildi',
    keyNotSet: 'Girilmedi',
    saveKeys: 'Anahtarları Kaydet',
    keysSaved: 'Anahtarlar kaydedildi.',
    removeKey: 'Kaldır',
    test: 'Bağlantıyı Dene',
    testOk: 'DNV {{status}} ile yanıt verdi. Anahtarlar çalışıyor.',
    testFailed: 'DNV çağrıyı kabul etmedi ({{code}}).',
    vessels: 'Gemiler',
    vesselsHint: 'Yalnız açık gemiler gönderilir.',
    readOnly: 'Bu ayarları yalnız Ayarlar düzenleme yetkisi olanlar değiştirebilir.',
  },
  vesselColumns: {
    vessel: 'Gemi',
    imo: 'IMO',
    send: 'Gönder',
    mapped: 'Eşlenen klas ekipmanı',
    pending: 'Henüz gönderilmedi',
    last: 'Son aktarım',
  },
  mapping: {
    hint: 'Her ekipmana DNV klas bileşen kodu (classcomponent) ve DNV VIS kodu girilebilir. Kendi klas kodu olmayan bileşen atasınınkini kullanır.',
    coverage: 'Klasla ilgili {{total}} ekipmandan {{mapped}} eşlendi',
    template: 'Eşlemeyi İndir (Excel)',
    import: 'Dosya Yükle',
    previewTitle: 'Eşleme içe aktarma önizlemesi',
    apply: 'Uygula',
    applied: 'Eşleme içe aktarıldı.',
    nothing: 'Dosya bir şey değiştirmiyor.',
    inherited: 'Atasından: {{code}}',
    save: 'Kaydet',
    saved: 'Kaydedildi',
    onlyClass: 'Yalnız klasla ilgili',
    readOnly: 'Eşlemeyi görebilirsiniz; değiştirmek için Sertifikalar düzenleme yetkisi gerekir.',
    columns: {
      code: 'Kod',
      name: 'Ekipman',
      sfi: 'SFI',
      classRelevant: 'Klas',
      classComponent: 'DNV Klas Bileşeni',
      visCode: 'DNV VIS Kodu',
      row: 'Satır',
      action: 'Değişiklik',
      error: 'Sorun',
    },
    actions: { create: 'Yeni', update: 'Değişir', unchanged: 'Değişmez' },
  },
  preview: {
    from: 'Yapılış başı',
    to: 'Yapılış sonu',
    last12: 'Son 12 ay',
    refresh: 'Yenile',
    records: 'Kayıt',
    errors: 'Hatalı',
    warnings: 'Uyarılı',
    sent: 'Gönderilmiş',
    empty: 'Bu aralıkta tamamlanmış iş kaydı yok.',
    ok: 'Hazır',
    sentBadge: 'Gönderildi',
    hint: 'Hatalı kayıt gönderilmez; kaynağı düzeltin (ör. gemi IMO’su ya da iş kodu), sonraki çalışmada gider.',
    columns: {
      wo: 'İE No.',
      done: 'Yapıldı',
      equipment: 'Ekipman',
      jobno: 'jobno',
      jobname: 'jobname',
      classcomponent: 'classcomponent',
      status: 'Doğrulama',
    },
  },
  transfers: {
    send: 'Şimdi Gönder',
    retry: 'Yeniden Dene',
    sentResult: 'Aktarım bitti: {{status}}.',
    empty: 'Henüz bir şey gönderilmedi.',
    retractions: 'Gönderildikten sonra iptal edilenler',
    retractionsHint:
      'DNV’de silme çağrısı yok. Gönderildikten sonra iptal edilen kayıt DNV’de kalır; kaldırılması gerekiyorsa DNV’ye (mpms@dnv.com) bildirin. Aynı iş ve tarihle giden düzeltme kaydı onun yerine geçer.',
    columns: {
      started: 'Başladı',
      trigger: 'Çalışma',
      status: 'Durum',
      records: 'Kayıt',
      batches: 'Çağrı',
      http: 'HTTP',
      error: 'Hata',
      next: 'Sonraki deneme',
      record: 'İş kaydı',
      sentAt: 'Gönderildi',
      voidedAt: 'İptal edildi',
      reason: 'Gerekçe',
    },
    trigger: { auto: 'Günlük', manual: 'Elle', retry: 'Yeniden deneme' },
    status: {
      running: 'Çalışıyor',
      succeeded: 'Gönderildi',
      failed: 'Başarısız',
      nothing_to_send: 'Gönderilecek yok',
    },
  },
  download: {
    hint: 'Önizlemedeki kayıtlar, DNV Interface Description sütun sırasıyla. Hatalı kayıtlar da dosyadadır; elle düzeltilebilir.',
    json: 'JSON İndir',
    xlsx: 'Excel İndir',
    csv: 'CSV İndir',
    last12: 'Son 12 ayı indir',
    last12Hint:
      'Tek tıkla son 12 ayın bütün tamamlanmış iş kayıtları, MAD JSON olarak (DNV-CP-0206 toplu aktarım).',
  },
  issues: {
    IMO_MISSING: 'Geminin IMO numarası yok.',
    CLASS_COMPONENT_MISSING: 'DNV klas bileşen kodu yok (kodsuz gönderilir).',
    JOB_NO_MISSING: 'İşin kodu yok.',
    TRUNCATED: 'Metin DNV uzunluğuna kısaltıldı.',
    REQUIRED: 'Zorunlu değer eksik.',
    INVALID_TYPE: 'Değer türü yanlış.',
    OUT_OF_RANGE: 'Değer aralık dışında.',
    INVALID_DATE: 'Geçersiz tarih.',
    TOO_LONG: 'Metin çok uzun.',
    INVALID_VALUE: 'İzin verilmeyen değer.',
    EMPTY_FILE: 'Dosya boş.',
    MISSING_COLUMN: 'Sütun eksik.',
    DUPLICATE_EQUIPMENT: 'Ekipman dosyada iki kez var.',
    INVALID_CODE: 'Yalnız harf, rakam ve . _ / - kullanın.',
    UNKNOWN_EQUIPMENT: 'Bu gemide böyle bir ekipman yok.',
  },
  errors: {
    MAD_DISABLED: 'Günlük gönderim kapalı. Ayarlar’dan açın.',
    MAD_KEYS_MISSING: 'API anahtarı bekleniyor: iki anahtarı Ayarlar’a girin.',
    MAD_COMPANY_MISSING: 'Ayarlar’a şirket adını girin.',
    MAD_VESSEL_DISABLED: 'Önce bu gemiyi Ayarlar’da açın.',
    MAD_TRANSFER_RUNNING: 'Bu geminin bir aktarımı zaten çalışıyor.',
    INVALID_MAD_SETTINGS: 'Ayarları denetleyin: şirket adı gerekli, uç adresi https olmalı.',
    INVALID_MAD_CODE: 'Yalnız harf, rakam ve . _ / - kullanın.',
    ROW_VERSION_CONFLICT: 'Bu arada başkası değiştirdi. Yeniden yükleyip deneyin.',
    FORBIDDEN: 'Bunun için yetkiniz yok.',
    GENERIC: 'Bir sorun oldu. Yeniden deneyin.',
  },
};
